import type { SalesChecklist } from "@expert/contentful";

import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import {
    DynamicCoreBenefits,
    DynamicToneGuidance,
    useDynamicSmartOffer,
    useOfferChecklistStore,
} from "@expert/sales-tools";
import { Accordion, Divider, Flex, ScrollArea, Text } from "@mantine/core";
import { type JSX, useCallback, useMemo, useState } from "react";

import { getRootDispatcher } from "@/analytics";
import { isSectionComplete } from "@/sales-tools/home-product/components/FullOfferChecklist/utils/isSectionComplete";
import { usePartner, useSessionId } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";

import { LanguageSelector } from "../../../language-selector/LanguageSelector";
import { ChecklistIcon } from "../Icons";
import { ChecklistSectionWithErrorBoundary } from "./components/ChecklistSectionWithErrorBoundary";

export type FullOfferChecklistProps = {
    salesChecklist: SalesChecklist;
};

export function FullOfferChecklist({ salesChecklist }: FullOfferChecklistProps): JSX.Element | null {
    const sessionId = useSessionId();
    const partner = usePartner();
    const logger = useLogger({ module: "useDynamicSmartOffer" });
    const {
        features: { isDynamicSmartOfferEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const {
        data: dynamicSmartOfferData,
        isPending,
        isAwaitingData,
        isEnabled,
        showLatest,
        hasNewerData,
    } = useDynamicSmartOffer({
        sessionId,
        salesChecklist,
        partner,
        dispatcher: getRootDispatcher(),
        logger,
        isDynamicSmartOfferEnabled,
    });

    const firstSectionToOpen = useMemo(
        () =>
            salesChecklist.checklistSections.find(
                (section) =>
                    section.checklistItems.length !== 0 &&
                    !isSectionComplete(section.checklistItems, useOfferChecklistStore.getState().getData(sessionId)),
            )?.title,
        [salesChecklist, sessionId],
    );

    const [accordionValue, setAccordionValue] = useState<string[] | undefined>(
        firstSectionToOpen ? [firstSectionToOpen] : undefined,
    );

    const onSectionComplete = useCallback(
        (title: string) => {
            setAccordionValue((prevState) => {
                const indexOfCompletedSection = salesChecklist.checklistSections.findIndex(
                    (section) => section.title === title,
                );
                const nextTitle = salesChecklist.checklistSections.at(indexOfCompletedSection + 1)?.title;
                if (nextTitle) {
                    prevState?.push(nextTitle);
                }

                if (prevState?.includes(title)) {
                    prevState = prevState.filter((t) => t !== title);
                }
                return prevState;
            });
        },
        [salesChecklist.checklistSections],
    );

    return (
        <Flex data-testid="step-two-content" direction="column" w="100%">
            <ScrollArea h="calc(100% - var(--customer-panel-bottom-control-footer-height))">
                <Flex id="full-offer-checklist-header" c="dark.2" p={"sm"} pb="xxs" justify="space-between">
                    <Flex align="center">
                        <ChecklistIcon />

                        <Text fw={700} fz="xs" pl="3xs">
                            OFFER CHECKLIST
                        </Text>
                    </Flex>

                    <LanguageSelector />
                </Flex>

                <Divider size="xs" color="dark.4" />

                <DynamicToneGuidance
                    isEnabled={isEnabled}
                    isPending={isPending}
                    isAwaitingData={isAwaitingData}
                    hasNewerData={hasNewerData}
                    toneGuidance={dynamicSmartOfferData.toneGuidance}
                    onShowLatest={showLatest}
                />

                <Accordion
                    transitionDuration={250}
                    multiple
                    value={accordionValue}
                    onChange={(section) => setAccordionValue(section)}
                >
                    {salesChecklist.checklistSections.map((section) => {
                        if (section.checklistItems.length === 0) return null;

                        return (
                            <ChecklistSectionWithErrorBoundary
                                key={section.id}
                                section={section}
                                onSectionComplete={onSectionComplete}
                                prependSlot={
                                    isDynamicCoreBenefitsSection(section) ? (
                                        <DynamicCoreBenefits
                                            isEnabled={isEnabled}
                                            isPending={isPending}
                                            isAwaitingData={isAwaitingData}
                                            pitchPoints={dynamicSmartOfferData.pitchPoints}
                                        />
                                    ) : undefined
                                }
                                hiddenItemKeys={
                                    isDynamicCoreBenefitsSection(section) && isEnabled ? ["COVERAGE"] : undefined
                                }
                            />
                        );
                    })}
                </Accordion>
            </ScrollArea>
        </Flex>
    );
}

function isDynamicCoreBenefitsSection(section: { sectionType?: string; enableDynamicContent?: boolean }) {
    return section.sectionType === "coreBenefits" && section.enableDynamicContent;
}
